import Image from "next/image";
import { FC, Fragment, useState } from "react";
import Button from "~/components/Button";
import ContactUsModal from "~/components/ContactUsModal";
import { Container } from "~/components/Container";
import Icon from "~/components/Icon";
import Text from "~/components/Text";
import {
  AddOns,
  Channels,
  Package,
  PackageType,
  PricingPlan,
} from "~/services/package";
import styles from "./PlansComparisonTable.module.scss";

interface PlansComparisonTableProps {
  packages: Package[];
}

const sections = [
  {
    section: "Chatbot Features",
    tag: "AI Assistant",
    items: [
      { name: "AI Agent", column: "ai_agent", height: 24 },
      { name: "AI Auto Reply", column: "ai_auto_reply", height: 24 },
      { name: "Livechat", column: "live_chat", height: 24 },
      { name: "Workflow", column: "workflow", height: 24 },
      { name: "Number of chatbots", column: "max_bot", height: 48 },
      { name: "Knowledge base", column: "max_bot_data", height: 48 },
      {
        name: "Bot for multiples brands",
        column: "bot_for_multiples_brand",
        height: 48,
      },
      {
        name: "Customized training model",
        column: "customized_model",
        height: 48,
      },
    ],
  },
  {
    section: "Helpdesk",
    items: [
      { name: "Chat management", column: AddOns.HELP_DESK, height: 48 },
      { name: "Ticket management", column: AddOns.TICKET, height: 48 },
      { name: "AI Suggestions", column: "ai_suggest", height: 24 },
      { name: "Total seats", column: "max_member", height: 24 },
      {
        name: "Total sessions",
        column: "max_monthly_conversation",
        height: 24,
      },
    ],
  },
  {
    section: "Powerful features",
    items: [
      { name: "Email box", column: AddOns.MAILBOX, height: 24 },
      { name: "White label", column: AddOns.WHITE_LABEL, height: 24 },
      { name: "API Access", column: "api_access", height: 24 },
    ],
  },
  {
    section: "Channels",
    items: [
      { name: "Website", column: Channels.WEBSITE, height: 24 },
      { name: "Mobile", column: Channels.MOBILE, height: 24 },
      { name: "Integrations", column: "integration", height: 24 },
    ],
  },
];

const BasedOnRequest = ({ height }: { height: number }) => {
  return (
    <div className="flex gap-2 items-center" style={{ height }}>
      <Icon name="check-circle" color="#702DFF" />
      <Text color="#702DFF">Based on request</Text>
    </div>
  );
};

const Checkbox = ({ height }: { height: number }) => {
  return (
    <div style={{ height }} className="flex items-center">
      <Icon name="check-circle" color="#3BA55C" size={24} />
    </div>
  );
};

const Empty = ({ height }: { height: number }) => {
  return (
    <div style={{ height }} className="flex items-center">
      <div className={styles.empty} />
    </div>
  );
};

const BenefitItem = ({
  pkg,
  column,
  height,
}: {
  pkg: Package;
  column: string;
  height: number;
}) => {
  switch (column) {
    case "max_bot": {
      return (
        <Text color="600" style={{ height }} className="flex items-center">
          {pkg.max_bot === -1
            ? "Unlimited Chatbot"
            : `${pkg.max_bot} ${
                pkg.max_bot === 1 ? "chatbot only" : "chatbots"
              }`}
        </Text>
      );
    }
    case "max_bot_data": {
      return (
        <Text color="600" style={{ height }} className="flex items-center">
          {pkg.max_bot_data === -1 ? "Unlimited" : `${pkg.max_bot_data}`}{" "}
          documents
        </Text>
      );
    }
    case "max_member": {
      return (
        <Text color="600" style={{ height }}>
          {pkg.max_member === -1
            ? "Unlimited seats"
            : `${pkg.max_member} ${
                pkg.max_member === 1 ? "seat only" : "seats"
              }`}
        </Text>
      );
    }
    case "max_monthly_conversation": {
      if (pkg.max_monthly_conversation === -1) {
        return <BasedOnRequest height={height} />;
      }

      return (
        <Text color="600" style={{ height }}>
          {pkg.max_monthly_conversation?.toLocaleString()} sessions / month
        </Text>
      );
    }
    case "integration": {
      return (
        <Text color="600" style={{ height }}>
          {pkg.features.integration}
        </Text>
      );
    }
    case AddOns.HELP_DESK:
    case AddOns.TICKET:
    case AddOns.WHITE_LABEL:
    case AddOns.MAILBOX:
    case "api_access": {
      if (
        pkg.features.add_ons?.includes(column as AddOns) ||
        pkg.type === PackageType.ENTERPRISE ||
        pkg.features[column as keyof typeof pkg.features]
      ) {
        return <Checkbox height={height} />;
      }

      return <Empty height={height} />;
    }
    case Channels.WEBSITE:
    case Channels.MOBILE: {
      if (pkg.features.channels?.includes(column)) {
        return <Checkbox height={height} />;
      }

      return <Empty height={height} />;
    }
    case "customized_model": {
      if (pkg.type === PackageType.ENTERPRISE) {
        return <BasedOnRequest height={height} />;
      }

      if (pkg.features[column as keyof typeof pkg.features]) {
        return <Checkbox height={height} />;
      }

      return <Empty height={height} />;
    }
    default: {
      if (pkg.features[column as keyof typeof pkg.features]) {
        return <Checkbox height={height} />;
      }

      return <Empty height={height} />;
    }
  }
};

const PlansComparisonTable: FC<PlansComparisonTableProps> = ({ packages }) => {
  const [showContactUs, setShowContactUs] = useState(false);

  const Pricing = () => (
    <div className={styles.feature_item}>
      <div className={styles.benefit}>
        <Text type="body-3" className="h-12">
          Price / month (billed <span className="font-semibold">annually</span>)
        </Text>
        <div className={styles.line} />
        <Text type="body-3" className="h-12">
          Price / month (billed <span className="font-semibold">monthly</span>)
        </Text>
      </div>

      {packages.map((pkg) => {
        const monthlyPrice = pkg.prices.find(
          (price) => price.plan === PricingPlan.MONTHLY
        )?.price;
        const annualPrice = pkg.prices.find(
          (price) => price.plan === PricingPlan.YEARLY
        )?.price;
        const annualPricePerMonth = annualPrice ? annualPrice / 12 : undefined;
        const isFree = pkg.type === PackageType.FREE;

        if (!monthlyPrice && !annualPricePerMonth && !isFree) {
          return (
            <div className={styles.benefit} key={pkg.id}>
              <Button
                onClick={() => setShowContactUs(true)}
                className="w-full"
                variant="secondary"
                type="outline"
              >
                Contact us
              </Button>
            </div>
          );
        }

        return (
          <div className={styles.benefit} key={pkg.id}>
            <Text
              type="title-2"
              color="600"
              className="flex gap-1 items-center h-12"
            >
              $
              {isFree
                ? "0,00"
                : annualPricePerMonth?.toFixed(2).replace(".", ",")}
              {!isFree && (
                <Text type="title-3" color="#3BA55C">
                  (-16%)
                </Text>
              )}
            </Text>
            <div className={styles.line} />
            <Text type="title-2" color="600" className="flex items-center h-12">
              $
              {isFree
                ? "0,00"
                : monthlyPrice?.toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                  })}
            </Text>
          </div>
        );
      })}
    </div>
  );

  return (
    <section className={styles.plans_comparison_table}>
      <Container className="flex flex-col gap-12">
        <div className="flex flex-col gap-2">
          <h2 className={styles.title}>Compare CX Genie plans</h2>
          <p className={styles.description}>
            Get started with the free plan. No credit card required.
          </p>
        </div>

        <div className={styles.table}>
          <div className={styles.header}>
            <div className={styles.item} />
            {packages.map((pkg) => (
              <div className={styles.item} key={pkg.id}>
                {pkg.type === PackageType.PRO && (
                  <div className={styles.tag}>
                    <Image
                      src="/svgs/sparks_2.svg"
                      width={20}
                      height={20}
                      alt="sparks"
                    />
                    <p className={styles.text}>Most Value</p>
                  </div>
                )}
                <p className={styles.text}>{pkg.name}</p>
              </div>
            ))}
          </div>
          <div className={styles.body}>
            <h3 className={styles.feature_name}>
              Pricing <span className="font-normal">(all prices in USD)</span>
            </h3>

            <Pricing />

            {sections.map((feature) => (
              <Fragment key={feature.section}>
                <div className={styles.feature_name}>
                  {feature.section}
                  {feature.tag && (
                    <div className={styles.tag}>
                      <Image
                        src="/svgs/sparks_2.svg"
                        width={20}
                        height={20}
                        alt="sparks"
                      />
                      <p className={styles.text}>{feature.tag}</p>
                    </div>
                  )}
                </div>

                <div className={styles.feature_item}>
                  <div className={styles.benefit}>
                    {feature.items.map((item) => (
                      <Fragment key={item.name}>
                        <Text
                          color="600"
                          style={{ height: item.height }}
                          className="flex items-center"
                        >
                          {item.name}
                        </Text>
                        <div className={styles.line} />
                      </Fragment>
                    ))}
                  </div>

                  {packages.map((pkg) => (
                    <div className={styles.benefit} key={pkg.id}>
                      {feature.items.map((item) => (
                        <Fragment key={item.name}>
                          <BenefitItem
                            pkg={pkg}
                            column={item.column}
                            height={item.height}
                          />
                          <div className={styles.line} />
                        </Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </Container>

      <ContactUsModal
        open={showContactUs}
        handleClose={() => setShowContactUs(false)}
      />
    </section>
  );
};

export default PlansComparisonTable;
